import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useOrganization } from '@/hooks/useOrganization';
import { subscriptionService } from '@/utils/subscriptionService';
import { DownloadUpgradeModal } from './DownloadUpgradeModal';
import { DownloadAccessContext, type DownloadAccessContextValue } from './DownloadAccessContext';

const BLOCKED_PLANS = new Set(['basic', 'free']);

export const DownloadAccessProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const { isOrganization } = useOrganization();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const recommendedPlan = useMemo<'Individual' | 'Corporate'>(
    () => (isOrganization ? 'Corporate' : 'Individual'),
    [isOrganization]
  );

  const resolveDownloadAccess = useCallback(async () => {
    if (!user) {
      setCanDownload(false);
      setLoaded(true);
      return false;
    }

    const [isSuperAdmin, planName] = await Promise.all([
      subscriptionService.isUserSuperAdmin(),
      subscriptionService.getPlanName(),
    ]);
    const allowed = isSuperAdmin || !BLOCKED_PLANS.has(planName.toLowerCase());
    setCanDownload(allowed);
    setLoaded(true);
    return allowed;
  }, [user]);

  useEffect(() => {
    setLoaded(false);
    void resolveDownloadAccess();
  }, [resolveDownloadAccess]);

  const guardDownload = useCallback(async () => {
    const allowed = loaded ? canDownload : await resolveDownloadAccess();
    if (!allowed) {
      setIsUpgradeModalOpen(true);
      return false;
    }
    return true;
  }, [canDownload, loaded, resolveDownloadAccess]);

  const contextValue = useMemo<DownloadAccessContextValue>(
    () => ({ guardDownload }),
    [guardDownload]
  );

  return (
    <DownloadAccessContext.Provider value={contextValue}>
      {children}
      <DownloadUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        recommendedPlan={recommendedPlan}
      />
    </DownloadAccessContext.Provider>
  );
};
